import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { FileSignature, Plus, Search, Trash2, Clock4 } from "lucide-react";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { ContractEditor } from "./ContractEditor";
import { contractsApi } from "../../lib/contractsApi";

const ST = {
  draft: ["مسودة", "bg-slate-100 text-slate-600"], sent: ["مُرسل للتوقيع", "bg-blue-100 text-blue-700"],
  signed: ["موقّع", "bg-emerald-100 text-emerald-700"], expired: ["منتهي", "bg-rose-100 text-rose-700"],
};
const soon = (d) => d && (new Date(d) - new Date()) / 864e5 <= 30 && new Date(d) >= new Date(new Date().toDateString());

export default function ContractsPanel() {
  const [rows, setRows] = useState([]);
  const [types, setTypes] = useState({});
  const [open, setOpen] = useState(null);
  const [q, setQ] = useState("");
  const [st, setSt] = useState("all");
  const load = useCallback(() => contractsApi.list().then(setRows).catch(() => toast.error("تعذّر تحميل العقود")), []);
  useEffect(() => { load(); contractsApi.types().then(setTypes); }, [load]);
  const list = useMemo(() => rows.filter((r) => (st === "all" || r.status_view === st)
    && (!q || `${r.title} ${r.party_name} ${r.number}`.toLowerCase().includes(q.toLowerCase()))), [rows, q, st]);
  const counts = useMemo(() => rows.reduce((a, r) => ({ ...a, [r.status_view]: (a[r.status_view] || 0) + 1 }), {}), [rows]);
  const del = async (r) => {
    if (!window.confirm(`حذف ${r.title}؟`)) return;
    try { await contractsApi.remove(r.id); load(); } catch (e) { toast.error(e?.response?.data?.detail || "تعذّر الحذف"); }
  };
  if (open) return <ContractEditor initial={open} types={types} onBack={() => { setOpen(null); load(); }} onSaved={load} />;
  return (
    <div className="space-y-4" data-testid="contracts-panel">
      <div className="flex flex-wrap items-center gap-2">
        <h2 className="font-heading text-xl font-bold text-slate-800 flex items-center gap-2"><FileSignature className="h-5 w-5 text-blue-600" /> إدارة العقود</h2>
        <Button onClick={() => setOpen({})} className="ms-auto gap-1.5 bg-blue-600 hover:bg-blue-700" data-testid="contract-new"><Plus className="h-4 w-4" /> عقد جديد</Button>
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-5 gap-2">
        {[["all", "الكل", rows.length], ...Object.entries(ST).map(([k, [l]]) => [k, l, counts[k] || 0])].map(([k, l, n]) => (
          <button key={k} onClick={() => setSt(k)} data-testid={`contracts-filter-${k}`}
            className={`rounded-xl border p-3 text-start transition-colors ${st === k ? "border-blue-500 bg-blue-50" : "bg-white hover:border-blue-300"}`}>
            <p className="text-xs text-slate-500">{l}</p><p className="font-bold text-lg tabular-nums">{n}</p>
          </button>
        ))}
      </div>
      <div className="relative max-w-sm"><Search className="absolute right-3 top-2.5 h-4 w-4 text-slate-400" /><Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="بحث بالعنوان أو الطرف أو الرقم" className="pr-9" data-testid="contracts-search" /></div>
      <div className="rounded-2xl border bg-white overflow-x-auto">
        <table className="w-full text-sm min-w-[720px]">
          <thead className="bg-slate-50 text-slate-500"><tr>{["الرقم", "العنوان", "النوع", "الطرف الثاني", "المدة", "الحالة", ""].map((h) => <th key={h} className="text-right px-3 py-2 font-medium">{h}</th>)}</tr></thead>
          <tbody>
            {list.map((r) => (
              <tr key={r.id} className="border-t hover:bg-slate-50 cursor-pointer" onClick={() => setOpen(r)} data-testid={`contract-row-${r.id}`}>
                <td className="px-3 py-2 tabular-nums text-slate-500">{r.number}</td>
                <td className="px-3 py-2 font-semibold text-slate-800">{r.title}</td>
                <td className="px-3 py-2 text-slate-600">{types[r.type] || r.type}</td>
                <td className="px-3 py-2">{r.party_name || "—"}</td>
                <td className="px-3 py-2 tabular-nums text-xs">{r.start_date || "—"} ← {r.end_date || "—"}
                  {r.status === "signed" && soon(r.end_date) && <span className="mr-1 inline-flex items-center gap-0.5 text-amber-700 font-semibold" data-testid={`contract-expiring-${r.id}`}><Clock4 className="h-3 w-3" /> ينتهي قريباً</span>}</td>
                <td className="px-3 py-2"><span className={`rounded-full px-2 py-0.5 text-xs font-semibold ${ST[r.status_view]?.[1]}`}>{ST[r.status_view]?.[0]}</span></td>
                <td className="px-3 py-2" onClick={(e) => e.stopPropagation()}>
                  {r.status !== "signed" && <button onClick={() => del(r)} className="text-slate-400 hover:text-rose-600" data-testid={`contract-delete-${r.id}`}><Trash2 className="h-4 w-4" /></button>}
                </td>
              </tr>
            ))}
            {!list.length && <tr><td colSpan={7} className="px-3 py-10 text-center text-slate-400">لا توجد عقود — ابدأ بـ «عقد جديد» واطلب من الوكيل الذكي صياغته.</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}
